import axios from 'axios';

import { moveAfterItemByItem, isEmptyObject, findInObj } from './utilities/utils';

window.theme.cart = window.theme.cart || {};
const singleItemLimit = 5;
// TO DO: return an error with the response if the quantity is adjusted down


window.cartUI = window.cartUI || {};
const REMOVAL_LINGER_MS = 500;

if (!window.cartUI.recentlyRemoved) {
  window.cartUI.recentlyRemoved = new Set();
}

let removalLingerTimer = null;

const markRecentlyRemoved = (key) => {
  window.cartUI.recentlyRemoved.add(key);
};

const resetRemovalLinger = () => {
  if (removalLingerTimer) {
    clearTimeout(removalLingerTimer);
  }
  removalLingerTimer = setTimeout(() => {
    window.cartUI.recentlyRemoved.clear();
    const snapshot = window.cartUI._lastAugmentedCart;
    if (snapshot) {
      const cart = JSON.parse(JSON.stringify(snapshot));
      Object.assign(window.theme.cart, cart);
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
    }
  }, REMOVAL_LINGER_MS);
};

const isRecentlyRemoved = (key) => window.cartUI.recentlyRemoved.has(key);

const addItem = async (variantID, quantity, extras = {}) => {
  // TO DO: Implement per-item limits here
  quantity = Math.min(quantity, singleItemLimit);

  // Check if we'd exceed the per-product limit
  const productLimit = findInObj(window.theme.cart.productLimits, pl => {
    return pl.variantIDs.indexOf(variantID) !== -1;
  });

  if (productLimit) {
    const { qtyInCart: PLqtyInCart, limit: PLlimit } = productLimit;

    if (PLqtyInCart >= PLlimit) {
      window.spawnDialog(`Sorry babe, you can only buy ${ PLlimit } of these at a time.`);
      window?.announcements?.error(`Sorry babe, you can only buy ${ PLlimit } of these at a time.`);
      return false;
    }

    const wouldQty = PLqtyInCart + quantity;
    if (wouldQty > PLlimit) {
      // Revise added quantity down
      quantity = Math.min(quantity, PLlimit);
      window.spawnDialog(`Hey babe, max for this is ${ PLlimit }, so we'll just add ${ quantity }.`);
      window?.announcements?.error(`Sorry babe, you can only buy ${ PLlimit } of these at a time.`);
    }
  }

  const result = await axios.post('/cart/add.js', { items: [{
    ...{ id: variantID, quantity: quantity },
    ...extras,
  }] });

  // window.analytics.change(result.data, quantity);
  // Handle analytics in augmentCart instead.

  window?.announcements?.success(`Added ${quantity} ${ quantity > 1 ? 'items' : 'item' } to your cart`);
  return result;
};

const updateItem = async (key, quantity) => {
  const existingItem = window.theme.cart.items.find(item => item.key === key);
  const limit = existingItem.limit ?? singleItemLimit;

  quantity = Math.max(0, quantity);
  quantity = Math.min(quantity, limit);
  const diff = quantity - existingItem.quantity;
  if (diff === 0) {
    // No change, return
    return false;
  }

  // Check if we'd exceed the per-product limit
  const productLimit = window.theme.cart.productLimits[existingItem.product_id];
  // Always allow decrements
  if (productLimit && diff > 0) {
    const { qtyInCart: PLqtyInCart, limit: PLlimit } = productLimit;

    if (PLqtyInCart >= PLlimit) {
      // Not allowed - would exceed product limit
      window.spawnDialog(`Sorry babe, you can only buy ${ PLlimit } of these at a time.`);
      return false;
    }

    const wouldQty = PLqtyInCart + diff;
    if (wouldQty > PLlimit) {
      // Revise down
      quantity = PLlimit - PLqtyInCart;
      window.spawnDialog(`Hey babe, max for this is ${ PLlimit }, so we'll just add ${ quantity }.`);
    }
  }

  const result = await axios.post('/cart/update.js', { updates: { [key]: quantity } });
  window.analytics.change(existingItem, diff);
  const action = quantity == 0 ? 'removed from' : 'updated in';
  window?.announcements?.success(`item ${action} your cart`);
  return result;
};

const changeItem = async (key, quantity, extras = {}) => {
  const existingItem = window.theme.cart.items.find(item => item.key === key);
  const limit = existingItem.limit ?? singleItemLimit;

  quantity = Math.min(quantity, limit);
  const diff = quantity - existingItem.quantity;
  if (diff === 0 && isEmptyObject(extras)) {
    // No change, return
    return false;
  }

  // Check if we'd exceed the per-product limit
  const productLimit = window.theme.cart.productLimits[existingItem.product_id];
  // Always allow decrements
  if (productLimit && diff > 0) {
    const { qtyInCart: PLqtyInCart, limit: PLlimit } = productLimit;

    if (PLqtyInCart >= PLlimit) {
      // Not allowed - would exceed product limit
      window.spawnDialog(`Sorry babe, you can only buy ${ PLlimit } of these at a time.`);
      return false;
    }

    const wouldQty = PLqtyInCart + diff;
    if (wouldQty > PLlimit) {
      // Revise down
      quantity = PLlimit - PLqtyInCart;
      window.spawnDialog(`Hey babe, max for this is ${ PLlimit }, so we'll just add ${ quantity }.`);
    }
  }

  const payload = {
    id: key,
    ...quantity && {
      quantity: quantity,
    },
    ...extras,
  };
  const result = await axios.post('/cart/change.js', payload);
  window.analytics.change(existingItem, diff);
  window?.announcements?.success(`item quantity updated in your cart`);
  return result;
};

const addOrChange = async(variantID, quantity, extras = {}) => {
  const existingItem = window.theme.cart.items.find(item => item.id === variantID);
  if (existingItem) {
    return await changeItem(existingItem.key, quantity + existingItem.quantity, extras);
  }
  return await addItem(variantID, quantity, extras);
};

const addItems = async(items) => {
  return await axios.post('/cart/add.js', { items });
};

const removeItem = async (key) => {
  markRecentlyRemoved(key);
  return await updateItem(key, 0);
};

const getUpsells = async () => {
  const response = await axios.get('/cart?view=nolayout.upsells');
  const upsells = response.data;
  return upsells;
};

const getCart = async () => {
  const response = await axios.get('/cart.js');
  return response.data;
};

const refreshCart = async cart => {
  cart = cart ?? await axios.get('/cart.js').then(resp => resp.data);
  cart = await augmentCart(cart);

  window.cartUI._lastAugmentedCart = JSON.parse(JSON.stringify(cart));

  if (window.cartUI?.recentlyRemoved?.size) {
    cart.items.forEach(item => {
      item.is_hidden = isRecentlyRemoved(item.key);
    });
  } else {
    cart.items.forEach(item => {
      if (item.is_hidden) item.is_hidden = false;
    });
  }

  Object.assign(window.theme.cart, cart);

  const event = new CustomEvent('cart:refresh', { detail: cart });
  document.dispatchEvent(event);
  // document.addEventListener('cart:refresh', e => {
  //   const { detail: cart } = e;
  //   Do something with the cart
  // });
};

const decideAfterpay = async () => {
  const response = await axios.get('/cart?view=nolayout.afterpay');
  const showAfterpay = response.data;
  return showAfterpay;
};

const decideKlarna = async () => {
  const response = await axios.get('/cart?view=nolayout.klarna');
  const showKlarna = response.data;
  return showKlarna;
};

const assessIsAllGiftCards = async () => {
  const response = await axios.get('/cart?view=nolayout.is_all_gift_cards');
  const cartIsAllGiftCards = response.data;
  return cartIsAllGiftCards;
};

const getCartAdditionalData = async () => {
  const response = await axios.get('/cart?view=nolayout.additional_data');
  const additionalData = response.data;
  return additionalData;
};

const augmentCart = async cart => {
  // Identify new items
  const prevCart = window.theme.cart;
  const prevCartIDs = prevCart.items.map(i => i.id);
  const prevCartUpsells = prevCart.items.filter(i => i.upsell && i.upsell.show).map(i => i.key);
  cart.items.forEach(item => {
    item.customAttributes = item.customAttributes || {};
    // Switched from using item.key to item.id to prevent add animation when updating line item properties
    item.customAttributes.new = prevCartIDs.indexOf(item.id) === -1;
    if (item.customAttributes.new) {
      window.analytics.change(item, item.quantity);
    }
    // Identify orphaned added upsells, to show upsells on
    if (item.properties._complete_the_set) {
      const originator = cart.items.find(i => i.key === item.properties._complete_the_set);
      if (!originator) {
        item.customAttributes.orphaned = true;
      }
    }
  });

  // Move promo items to the top
  const promoItems = cart.items.filter(i => i.properties?._promo === 'bday25d5');
  const nonPromoItems = cart.items.filter(i => i.properties?._promo !== 'bday25d5');
  cart.items = [...promoItems, ...nonPromoItems];

  // Order items based on upsells
  // To Do: Resolve orphaned upsells changing places to the top - occurs because they are unbound
  // from the originator, and therefore position.
  // Shouldn't happen often because people are less likely to remove the originator, so leaving it for the moment.
  const addedUpsells = cart.items.filter(i => i.properties._complete_the_set);
  addedUpsells.forEach(upsell => {
    const originator = cart.items.find(i => i.key === upsell.properties._complete_the_set);
    moveAfterItemByItem(cart.items, upsell, originator);
  });

  // To Do: Relocate awaits to a Promise.all to run in parallel
  // To Do: Conditionally run operations (e.g. don't get upsell)
  const operations = [
    getUpsells(),
    decideAfterpay(),
    decideKlarna(),
    assessIsAllGiftCards(),
    getCartAdditionalData(),
  ];

  let upsells, additionalData;

  [
    upsells,
    cart.showAfterpay,
    cart.showKlarna,
    cart.isAllGiftCards,
    additionalData,
  ] = await Promise.all(operations);

  // if (we want to get upsell) {
    if (upsells) {
      Object.entries(upsells).forEach(([k,v]) => {
        const originatorItem = cart.items.find(item => item.product_id === parseInt(k));
        // To Do: Don't mutate unless different
        originatorItem.upsell = { ...v,
          ...{
            show: false,
            new: (prevCartUpsells.indexOf(originatorItem.key) === -1),
          }
        };
      });
    }
  // }

  // Decide which upsell(s) to show
  let chooseFromUpsells = cart.items.filter(item => item.upsell);
  // - Don't show dismissed upsells
  chooseFromUpsells = chooseFromUpsells.filter(item => !item.properties._dismiss_set);
  // - No upsells for items that already have an upsell added - so user is not presented with more than 1 upsell per item
  const addedUpsellKeys = addedUpsells.map(upsell => upsell.properties._complete_the_set);
  chooseFromUpsells = chooseFromUpsells.filter(item => addedUpsellKeys.indexOf(item.key) === -1);
  // - Don't show upsells on added upsells, unless orphaned
  chooseFromUpsells = chooseFromUpsells.filter(item => !item.properties._complete_the_set || item.customAttributes.orphaned);
  // - Only show most recent upsell
  chooseFromUpsells = chooseFromUpsells.slice(0,1);

  chooseFromUpsells.forEach(item => item.upsell.show = true);

  cart.items.forEach(item => {
    const additionalDataItem = additionalData.items.find(i => i.key === item.key);
    Object.assign(item, { ...additionalDataItem, ...item });
  });

  // Generate an array of product limits to reference
  const productLimits = {};
  cart.items.filter(item => item.product_limit).forEach(item => {
    productLimits[item.product_id] = productLimits[item.product_id] ?? { limit: item.product_limit, qtyInCart: 0, variantIDs: item.product_variants };
    productLimits[item.product_id].qtyInCart += item.quantity;
  });
  quietLog(productLimits);
  cart.productLimits = productLimits;

  const points_factor = window.theme?.loyaltyPointsFactor || 1.0;

  const originalPointsTotal = cart.items.reduce((totalPoints, item) => {
    try {
      const { points, quantity } = item;

      if (!(points && points !== '')) {
        return totalPoints;
      }

      return totalPoints + (points * points_factor * quantity);  
    } catch (err) {
      console.warn(err);
    }
    return totalPoints;
  }, 0);
  cart.points = Math.floor(originalPointsTotal * (cart.total_price / cart.original_total_price));

  cart.items.forEach(item => {
    try {
      const { points, quantity, line_price, original_line_price } = item;
      const points_factor = window.theme?.loyaltyPointsFactor || 1.0;
      item.adjustedPoints = Math.floor((points * points_factor * quantity) * (line_price / original_line_price));
    } catch(err) {
      console.warn(err);
    }
  });

  if (window?.config?.bdayPromos?.bhgs) {
    const { 
      hoodie_tag: bhgsHoodieTag, 
      sweatpants_tag: bhgsSweatpantsTag, 
    } = window.config.bdayPromos.bhgs;

    // Buy Hoodie, Get 50% Off Sweatpants logic
    const hoodiesQuantity = cart.items
      .filter(item => item.tags.includes(bhgsHoodieTag))
      .reduce((total, item) => total + item.quantity, 0)
    ;
    const sweatpantsQuantity = cart.items
      .filter(item => item.tags.includes(bhgsSweatpantsTag))
      .reduce((total, item) => total + item.quantity, 0)
    ;
    cart.bhgsEligible = hoodiesQuantity > sweatpantsQuantity;
  }
  
  

  return cart;
};

const clearCart = async () => {
  const result = await axios.post('/cart/clear.js');
  // TO DO: Clear analytics
  return result;
};

const applyDiscountCode = async (discountCode) => {
  const result = await axios.get(`/discount/${ discountCode }`);
  return result;
};

const updateItems = async (updates) => {
  if (!updates) {
    return false;
  }
  const result = await axios.post(`${window.Shopify.routes.root}cart/update.js`, { updates });
  return result;
}

const doAndRefresh = async (f, args) => {
  const response = await f(...args);

  if (!response) {
    // No action taken
    quietLog('No action taken');
    window?.announcements?.error('Unable to update cart. Please try again.')
    // TO DO: Return { error: }
    return false;
  }

  const data = response.data;

  // Bandaid way to tell if the response is the cart or not
  // TO DO: Consider mutating existing cart rather than getting the cart fresh.
  const dataIsCart = !!data.token;
  const cart = dataIsCart ? data : await getCart();

  const refreshed = await refreshCart(cart);

  resetRemovalLinger();

  return refreshed;
};

window.cartUtils = {
  updateItem: (...args) => doAndRefresh(updateItem, args),
  changeItem: (...args) => doAndRefresh(changeItem, args),
  removeItem: (...args) => doAndRefresh(removeItem, args),
  addItem: (...args) => doAndRefresh(addItem, args),
  addOrChange: (...args) => doAndRefresh(addOrChange, args),
  getUpsells: getUpsells,
  refreshCart: refreshCart,
  clearCart: (...args) => doAndRefresh(clearCart, args),
  applyDiscountCode,
  addItems: (...args) => doAndRefresh(addItems, args),
  updateItems: (...args) => doAndRefresh(updateItems, args),
}

refreshCart();

const checkBirthdayGwp = async (cart) => {
  const gwp = window.theme?.birthday;
  const promoProperty = gwp?.property || 'bday25d5';
  
  if (!gwp?.enabled) {
    const giftItem = cart?.items?.find(i => i.properties?._promo === promoProperty);
    if (giftItem) {
      await window.cartUtils.removeItem(giftItem.key);
    }
    return;
  }
  
  // Simple currency conversion using Shopify's rate
  const originalMinSpend = parseFloat(gwp.minSpend);
  let minSpend = originalMinSpend;
  
  if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
    const rate = parseFloat(window.Shopify.currency.rate);
    minSpend = Math.ceil(originalMinSpend * rate);
  }
  
  const collectionHandle = gwp.collection;
  const giftItem = cart.items.find(i => i.properties?._promo === promoProperty);

  if (cart.total_price / 100 >= minSpend) {
    if (!giftItem) {
      const resp = await axios.get(`/collections/${collectionHandle}/products.json`);
      if (!resp.data.products?.length) return;
      const random = resp.data.products[Math.floor(Math.random() * resp.data.products.length)];
      const firstVariant = random.variants[0];
      await window.cartUtils.addItem(firstVariant.id, 1, { properties: { _promo: promoProperty } });
    }
  } else if (giftItem) {
    await window.cartUtils.removeItem(giftItem.key);
  }
};

document.addEventListener('cart:refresh', e => checkBirthdayGwp(e.detail));

